import { RdxGenVersion } from "../../../../api/graphql/devices/devices";
import { WorkplaceFields } from "../../../../api/graphql/workplaces";
import { getWorkplaceChildren } from "../../../Workplaces/helpers/getWorkplaceChildren";

export interface DeviceTreeDevice {
  id: string;
  // The hardware ID that UpdateRDXDevicesDateTimeSettings takes
  deviceId?: string;
  name: string;
  // Decides the locale tag the device is sent
  softwareInfo?: { rdxGenVersion?: RdxGenVersion | null } | null;
}

export interface DeviceTreeNode {
  id: string;
  name: string;
  type: WorkplaceFields["__typename"] | "Unassigned";
  // The compatible devices at or under this node. Empty means the node shows
  // without a checkbox.
  devices: DeviceTreeDevice[];
  children: DeviceTreeNode[];
}

// Date & time is Room Display X only, so wayfinders are left out with desks
const HIDDEN_WORKPLACES = new Set(["Desk", "Wayfinder"]);

const toDeviceNode = ({ id, name }: DeviceTreeDevice): DeviceTreeNode => ({
  id,
  name,
  type: "Device",
  devices: [{ id, name }],
  children: [],
});

// The picker tree. Compatible devices hang under the room they're assigned to,
// and desks and wayfinders are left out. Compatible devices that aren't found
// in the hierarchy go under Unassigned/other, so each one is listed exactly once.
export const buildDeviceTree = (
  rootWorkplaces: WorkplaceFields[][],
  compatibleDevices: DeviceTreeDevice[]
) => {
  const compatibleIds = new Set(compatibleDevices.map((device) => device.id));
  const placedIds = new Set<string>();

  const toHeldDeviceNodes = (devices: DeviceTreeDevice[] = []) => {
    const nodes: DeviceTreeNode[] = [];

    devices.forEach((device) => {
      if (compatibleIds.has(device.id) && !placedIds.has(device.id)) {
        placedIds.add(device.id);
        nodes.push(toDeviceNode(device));
      }
    });

    return nodes;
  };

  const toNode = (workplace: WorkplaceFields): DeviceTreeNode => {
    const children =
      workplace.__typename === "Room"
        ? toHeldDeviceNodes(workplace.devices)
        : toNodes(getWorkplaceChildren(workplace));

    return {
      id: workplace.id,
      name: workplace.name,
      type: workplace.__typename,
      devices: children.flatMap((child) => child.devices),
      children,
    };
  };

  const toNodes = (groups: (WorkplaceFields[] | undefined)[]) =>
    groups.flatMap((group = []) =>
      group
        .filter((workplace) => !HIDDEN_WORKPLACES.has(workplace.__typename))
        .map(toNode)
    );

  const hierarchy = toNodes(rootWorkplaces);
  const unassignedNodes = compatibleDevices
    .filter((device) => !placedIds.has(device.id))
    .map(toDeviceNode);

  const unassigned: DeviceTreeNode | undefined = unassignedNodes.length
    ? {
        id: "unassigned-other",
        name: "Unassigned/other",
        type: "Unassigned",
        devices: unassignedNodes.flatMap((node) => node.devices),
        children: unassignedNodes,
      }
    : undefined;

  return { hierarchy, unassigned };
};

// A node is checked when any of its devices is selected
export const hasSelectedDevice = (
  node: DeviceTreeNode,
  selectedIds: ReadonlySet<string>
) => node.devices.some((device) => selectedIds.has(device.id));

// Selects all of the node's devices when none are selected, otherwise clears them
export const toggleNodeSelection = (
  selected: DeviceTreeDevice[],
  node: DeviceTreeNode
) => {
  const nodeIds = new Set(node.devices.map((device) => device.id));

  if (selected.some((device) => nodeIds.has(device.id))) {
    return selected.filter((device) => !nodeIds.has(device.id));
  }

  return [...selected, ...node.devices];
};
